/**
 * RBAC Role Management Dashboard
 *
 * Complete dashboard for managing tenant-wide RBAC roles and permissions.
 * FERP Architecture:
 * - Repository pattern (RoleRepository for all data access)
 * - Backend ID generation (Snowflake IDs only)
 * - Data-driven design (all role definitions from backend)
 * - Immutable audit trails (all operations logged)
 * - Life-critical safety (role changes affect access control)
 *
 * Week 2 RBAC Implementation - Frontend Component
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Badge,
  Tabs,
  Box,
  Alert,
  LoadingOverlay,
  Button,
} from '@mantine/core';
import {
  IconShield,
  IconUsers,
  IconChartBar,
  IconClipboardList,
  IconAlertCircle,
  IconPlus,
  IconRefresh,
  IconUpload,
  IconHistory,
  IconTemplate,
  IconEdit,
  IconClock,
  IconLock,
  IconUserCheck,
  IconHierarchy,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import { useRBAC } from '../hooks/useRBAC';
import { RoleListPanel } from './RoleListPanel';
import { UserRoleAssignmentPanel } from './UserRoleAssignmentPanel';
import { PermissionManagementPanel } from './PermissionManagementPanel';
import { RBACAuditLogPanel } from './RBACAuditLogPanel';
import { BulkUploadPanel } from './BulkUploadPanel';
import { BulkProgressTracker } from './BulkProgressTracker';
import { BulkResultsPanel } from './BulkResultsPanel';
import { BulkHistoryPanel } from './BulkHistoryPanel';
import { RoleTemplateLibraryPanel } from './RoleTemplateLibraryPanel';
import { RoleTemplateEditorPanel } from './RoleTemplateEditorPanel';
import { JITAccessRequestPanel } from './JITAccessRequestPanel';
import { JITApprovalQueuePanel } from './JITApprovalQueuePanel';
import { JITActiveSessionsPanel } from './JITActiveSessionsPanel';
import { RoleHierarchyVisualization } from './hierarchy/RoleHierarchyVisualization';
import {
  BulkOperationRepository,
  BulkOperation,
  ProcessingModeInput,
} from '../data/repositories/BulkOperationRepository';
import { RoleTemplate } from '../data/repositories/RoleTemplateRepository';

/**
 * Dashboard props
 * FERP: tenantId required for multi-tenant isolation
 */
export interface RoleManagementDashboardProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId?: string;
  /** Enable automatic polling for real-time updates */
  autoRefresh?: boolean;
  /** Polling interval in milliseconds (default: 2000ms) */
  pollingInterval?: number;
  /** Callback when role is selected */
  onRoleSelect?: (roleId: string) => void;
  /** Callback when role is created */
  onRoleCreate?: (roleId: string) => void;
  /**
   * Override permission check from portal auth context.
   * When true, bypasses the useRBAC permission gate (e.g., admin/architect ViewLayer).
   * The portal wrapper knows the user's real auth role even when RBAC backend is down.
   */
  allowAccess?: boolean;
}

/**
 * RoleManagementDashboard Component
 *
 * Provides comprehensive RBAC management:
 * - Role list and CRUD operations
 * - Permission management
 * - User role assignments
 * - Audit log viewing
 * - Role inheritance visualization
 *
 * FERP Compliance:
 * - All data via RoleRepository (no direct API calls)
 * - Backend Snowflake IDs for all roles
 * - Complete audit trail for all changes
 * - Polling-based real-time updates
 * - Life-critical validation for admin operations
 */
export function RoleManagementDashboard({
  tenantId: propTenantId,
  autoRefresh = true,
  pollingInterval = 2000,
  onRoleSelect,
  onRoleCreate,
  allowAccess = false,
}: RoleManagementDashboardProps) {
  // Apollo client for repositories
  const apolloClient = useApolloClient() as ApolloClient;
  const bulkOperationRepository = useMemo(
    () => new BulkOperationRepository(apolloClient),
    [apolloClient]
  );

  // Get tenant from context or props
  // FERP: Multi-tenant isolation via tenantId
  const tenantId = propTenantId || 'default-tenant'; // TODO: Get from useCurrentTenant hook

  // Active tab state
  const [activeTab, setActiveTab] = useState<string | null>('roles');

  // Selected role state
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  // Bulk operation state
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [completedOperation, setCompletedOperation] = useState<BulkOperation | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [viewingOperation, setViewingOperation] = useState<BulkOperation | null>(null);

  // Template management state
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RoleTemplate | null>(null);

  // Backend availability detection
  // FERP: Graceful degradation when GraphQL server isn't running
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // RBAC hook for current user permissions
  // FERP: Check if current user has admin access to manage roles
  // Skip polling if backend unavailable (prevents infinite error loops)
  const {
    currentRole,
    isAdmin,
    hasPermission,
    loading: rbacLoading,
    error: rbacError,
    refresh: refreshRBAC,
  } = useRBAC(tenantId, {
    skip: !backendAvailable && retryCount >= 1, // Stop after first failed attempt
    pollInterval: backendAvailable && autoRefresh ? pollingInterval : undefined,
  });

  // Detect backend unavailability
  React.useEffect(() => {
    if (rbacError) {
      // Check for common backend unavailability errors
      const isBackendError =
        rbacError.message.includes('Unknown field') || // GraphQL schema missing
        rbacError.message.includes('Failed to fetch') || // Network error
        rbacError.message.includes('Network error') || // Apollo network error
        rbacError.message.includes('ECONNREFUSED'); // Connection refused

      if (isBackendError) {
        setRetryCount((prev) => prev + 1);
        if (retryCount >= 0) {
          // Mark backend unavailable after first error (retryCount starts at 0)
          setBackendAvailable(false);
        }
      }
    }
  }, [rbacError, retryCount]);

  /**
   * Handle role selection
   * FERP: Callback with backend-generated Snowflake ID
   */
  const handleRoleSelect = (roleId: string) => {
    setSelectedRoleId(roleId);
    onRoleSelect?.(roleId);
  };

  /**
   * Handle role creation
   * FERP: Backend mutation with audit trail
   */
  const handleRoleCreate = () => {
    // TODO: Open create role modal
    console.log('Create role clicked');
  };

  /**
   * Handle refresh
   * FERP: Manual refresh trigger for user-initiated updates
   */
  const handleRefresh = async () => {
    await refreshRBAC();
  };

  /**
   * Handle bulk upload
   * FERP: Initiate bulk operation via repository
   */
  const handleBulkUpload = useCallback(
    async (file: File, csvContent: string, processingMode: ProcessingModeInput) => {
      setIsProcessing(true);
      try {
        const operation = await bulkOperationRepository.initiateBulkOperation({
          tenantId,
          csvContent,
          processingMode,
          filename: file.name,
        });

        setActiveBatchId(operation.batchId);
        setCompletedOperation(null);
      } catch (error) {
        console.error('[RoleManagementDashboard] Failed to initiate bulk operation:', error);
        alert('Failed to initiate bulk operation: ' + (error as Error).message);
      } finally {
        setIsProcessing(false);
      }
    },
    [bulkOperationRepository, tenantId]
  );

  /**
   * Handle bulk operation complete
   * FERP: Update state when operation completes
   */
  const handleOperationComplete = useCallback((operation: BulkOperation) => {
    setCompletedOperation(operation);
    setActiveBatchId(null);
  }, []);

  /**
   * Handle cancel bulk operation
   * FERP: Cancel in-progress operation
   */
  const handleCancelOperation = useCallback(
    async (batchId: string) => {
      try {
        await bulkOperationRepository.cancelBulkOperation(batchId, tenantId);
        setActiveBatchId(null);
      } catch (error) {
        console.error('[RoleManagementDashboard] Failed to cancel operation:', error);
        alert('Failed to cancel operation: ' + (error as Error).message);
      }
    },
    [bulkOperationRepository, tenantId]
  );

  /**
   * Handle rollback bulk operation
   * FERP: Rollback completed operation
   */
  const handleRollback = useCallback(
    async (batchId: string, reason: string) => {
      setIsRollingBack(true);
      try {
        await bulkOperationRepository.rollbackBulkOperation({
          batchId,
          tenantId,
          reason,
        });
        alert('Rollback successful! All assignments have been revoked.');
        setCompletedOperation(null);
      } catch (error) {
        console.error('[RoleManagementDashboard] Failed to rollback operation:', error);
        alert('Failed to rollback operation: ' + (error as Error).message);
      } finally {
        setIsRollingBack(false);
      }
    },
    [bulkOperationRepository, tenantId]
  );

  /**
   * Handle view operation details
   * FERP: View operation from history
   */
  const handleViewDetails = useCallback((operation: BulkOperation) => {
    setViewingOperation(operation);
    setActiveTab('bulk-results');
  }, []);

  /**
   * Handle template instantiate
   * FERP: Template instantiated as role
   */
  const handleTemplateInstantiate = useCallback((roleId: string) => {
    setSelectedRoleId(roleId);
    setActiveTab('roles');
    if (onRoleCreate) {
      onRoleCreate(roleId);
    }
  }, [onRoleCreate]);

  /**
   * Handle template save
   * FERP: Template created or edited
   */
  const handleTemplateSave = useCallback((templateId: string) => {
    setShowTemplateEditor(false);
    setEditingTemplate(null);
    setActiveTab('templates');
  }, []);

  /**
   * Handle create template
   * FERP: Open editor in create mode
   */
  const handleCreateTemplate = useCallback(() => {
    setEditingTemplate(null);
    setShowTemplateEditor(true);
    setActiveTab('template-editor');
  }, []);

  // Check if user has permission to manage roles
  // allowAccess: portal-level override when ViewLayer is admin/architect (useRBAC may fallback to viewer when backend is down)
  const canManageRoles = allowAccess || isAdmin || hasPermission('tenant:admin');

  // Backend unavailable - Development mode message
  if (!backendAvailable) {
    return (
      <Container size="xl" px="md">
        <Alert
          color="yellow"
          title="⚠️ RBAC Backend Not Running (Development Mode)"
          icon={<IconAlertCircle size={16} />}
        >
          <Text size="sm" fw={500} mb="xs">
            The RBAC GraphQL backend is not currently running.
          </Text>
          <Text size="sm" mb="md">
            RBAC requires the GraphQL gateway with RBAC schema to be running. The frontend components are
            working correctly, but cannot fetch data without the backend.
          </Text>

          <Box
            p="sm"
            style={{
              backgroundColor: 'var(--mantine-color-dark-8)',
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: 12,
            }}
          >
            <Text c="green" mb="xs">
              # Start the GraphQL backend:
            </Text>
            <Text c="dimmed">cd graphql_gateway</Text>
            <Text c="dimmed">cargo run</Text>
            <Text c="dimmed" mt="sm">
              # Then navigate to:
            </Text>
            <Text c="blue">http://localhost:3000/rbac</Text>
          </Box>

          <Text size="sm" mt="md" c="dimmed">
            <strong>What&apos;s Working:</strong> Frontend components, routing, navigation, error handling
          </Text>
          <Text size="sm" c="dimmed">
            <strong>What&apos;s Missing:</strong> Backend GraphQL server with RBAC queries (listRoles,
            getUserRoles, currentUserRole)
          </Text>

          <Group mt="md" gap="xs">
            <Button
              variant="light"
              size="sm"
              leftSection={<IconRefresh size={14} />}
              onClick={() => {
                setBackendAvailable(true);
                setRetryCount(0);
                refreshRBAC();
              }}
            >
              Retry Connection
            </Button>
            <Text size="xs" c="dimmed">
              (Click after starting backend)
            </Text>
          </Group>
        </Alert>
      </Container>
    );
  }

  // Error state (other errors)
  if (rbacError) {
    return (
      <Container size="xl" px="md">
        <Alert
          color="red"
          title="Error Loading RBAC Dashboard"
          icon={<IconAlertCircle size={16} />}
        >
          {rbacError.message}
          <Text size="sm" mt="xs">
            Please try refreshing the page. If the issue persists, contact support.
          </Text>
        </Alert>
      </Container>
    );
  }

  // Permission denied state
  if (!rbacLoading && !canManageRoles) {
    return (
      <Container size="xl" px="md">
        <Alert
          color="orange"
          title="Insufficient Permissions"
          icon={<IconAlertCircle size={16} />}
        >
          <Text size="sm">
            You do not have permission to manage roles. Current role: <strong>{currentRole}</strong>
          </Text>
          <Text size="sm" mt="xs">
            Required permission: <strong>tenant:admin</strong>
          </Text>
          <Text size="sm" mt="xs">
            Please contact your tenant administrator if you need access to role management.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" px="md">
      <LoadingOverlay visible={rbacLoading} />

      {/* Header */}
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Role Management Dashboard</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Manage tenant-wide RBAC roles, permissions, and user assignments
          </Text>
        </div>
        <Group gap="xs">
          <Badge color="blue" size="lg" leftSection={<IconShield size={14} />}>
            {currentRole}
          </Badge>
          <Button
            variant="light"
            size="sm"
            leftSection={<IconRefresh size={14} />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          {canManageRoles && activeTab === 'templates' && (
            <Button
              variant="filled"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={handleCreateTemplate}
            >
              Create Template
            </Button>
          )}
          {canManageRoles && activeTab === 'roles' && (
            <Button
              variant="filled"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={handleRoleCreate}
            >
              Create Role
            </Button>
          )}
        </Group>
      </Group>

      {/* Admin Notice */}
      {isAdmin && (
        <Alert color="blue" title="Administrator Access" icon={<IconShield size={16} />} mb="md">
          You have full administrator access to manage all roles and permissions for this tenant.
          All changes will be logged in the audit trail.
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="roles" leftSection={<IconShield size={16} />}>
            Roles
          </Tabs.Tab>
          <Tabs.Tab value="templates" leftSection={<IconTemplate size={16} />}>
            Role Templates
          </Tabs.Tab>
          <Tabs.Tab value="template-editor" leftSection={<IconEdit size={16} />}>
            Template Editor
          </Tabs.Tab>
          <Tabs.Tab value="assignments" leftSection={<IconUsers size={16} />}>
            User Assignments
          </Tabs.Tab>
          <Tabs.Tab value="bulk-upload" leftSection={<IconUpload size={16} />}>
            Bulk Assignment
          </Tabs.Tab>
          <Tabs.Tab value="bulk-history" leftSection={<IconHistory size={16} />}>
            Bulk History
          </Tabs.Tab>
          <Tabs.Tab value="permissions" leftSection={<IconChartBar size={16} />}>
            Permissions
          </Tabs.Tab>
          <Tabs.Tab value="jit-request" leftSection={<IconClock size={16} />}>
            JIT Request
          </Tabs.Tab>
          <Tabs.Tab value="jit-approvals" leftSection={<IconUserCheck size={16} />}>
            JIT Approvals
          </Tabs.Tab>
          <Tabs.Tab value="jit-sessions" leftSection={<IconLock size={16} />}>
            JIT Sessions
          </Tabs.Tab>
          <Tabs.Tab value="hierarchy" leftSection={<IconHierarchy size={16} />}>
            Hierarchy Visualization
          </Tabs.Tab>
          <Tabs.Tab value="audit" leftSection={<IconClipboardList size={16} />}>
            Audit Log
          </Tabs.Tab>
        </Tabs.List>

        {/* Roles Tab */}
        <Tabs.Panel value="roles" pt="md">
          <RoleListPanel
            tenantId={tenantId}
            selectedRoleId={selectedRoleId}
            onRoleSelect={handleRoleSelect}
            onRoleCreate={onRoleCreate}
            isLoading={rbacLoading}
          />
        </Tabs.Panel>

        {/* Role Templates Tab */}
        <Tabs.Panel value="templates" pt="md">
          <RoleTemplateLibraryPanel
            tenantId={tenantId}
            onTemplateInstantiate={handleTemplateInstantiate}
          />
        </Tabs.Panel>

        {/* Template Editor Tab */}
        <Tabs.Panel value="template-editor" pt="md">
          <RoleTemplateEditorPanel
            tenantId={tenantId}
            template={editingTemplate}
            onSave={handleTemplateSave}
            onCancel={() => {
              setShowTemplateEditor(false);
              setEditingTemplate(null);
              setActiveTab('templates');
            }}
          />
        </Tabs.Panel>

        {/* User Assignments Tab */}
        <Tabs.Panel value="assignments" pt="md">
          <UserRoleAssignmentPanel tenantId={tenantId} isLoading={rbacLoading} />
        </Tabs.Panel>

        {/* Bulk Upload Tab */}
        <Tabs.Panel value="bulk-upload" pt="md">
          <BulkUploadPanel
            tenantId={tenantId}
            onUpload={handleBulkUpload}
            isProcessing={isProcessing}
          />

          {activeBatchId && (
            <Box mt="md">
              <BulkProgressTracker
                batchId={activeBatchId}
                tenantId={tenantId}
                onComplete={handleOperationComplete}
                onCancel={handleCancelOperation}
              />
            </Box>
          )}

          {completedOperation && (
            <Box mt="md">
              <BulkResultsPanel
                operation={completedOperation}
                onRollback={handleRollback}
                isRollingBack={isRollingBack}
              />
            </Box>
          )}
        </Tabs.Panel>

        {/* Bulk History Tab */}
        <Tabs.Panel value="bulk-history" pt="md">
          <BulkHistoryPanel
            tenantId={tenantId}
            onViewDetails={handleViewDetails}
            onRollback={handleRollback}
          />
        </Tabs.Panel>

        {/* Bulk Results Tab (for viewing from history) */}
        <Tabs.Panel value="bulk-results" pt="md">
          {viewingOperation && (
            <BulkResultsPanel
              operation={viewingOperation}
              onRollback={handleRollback}
              isRollingBack={isRollingBack}
            />
          )}
        </Tabs.Panel>

        {/* Permissions Tab */}
        <Tabs.Panel value="permissions" pt="md">
          <PermissionManagementPanel tenantId={tenantId} isLoading={rbacLoading} />
        </Tabs.Panel>

        {/* JIT Request Tab */}
        <Tabs.Panel value="jit-request" pt="md">
          <JITAccessRequestPanel
            tenantId={tenantId}
            userId={`user_${currentRole}`} // TODO: Get actual user ID from context
            userEmail="user@example.com" // TODO: Get from context
            currentRole={currentRole ?? ''}
          />
        </Tabs.Panel>

        {/* JIT Approvals Tab */}
        <Tabs.Panel value="jit-approvals" pt="md">
          <JITApprovalQueuePanel
            tenantId={tenantId}
            approverId={`user_${currentRole}`} // TODO: Get actual user ID from context
            approverEmail="approver@example.com" // TODO: Get from context
          />
        </Tabs.Panel>

        {/* JIT Sessions Tab */}
        <Tabs.Panel value="jit-sessions" pt="md">
          <JITActiveSessionsPanel
            tenantId={tenantId}
            userId={`user_${currentRole}`} // TODO: Get actual user ID from context
            showOnlyMine={false}
          />
        </Tabs.Panel>

        {/* Hierarchy Visualization Tab */}
        <Tabs.Panel value="hierarchy" pt="md">
          <RoleHierarchyVisualization
            tenantId={tenantId}
            currentUser={`user_${currentRole}`}
          />
        </Tabs.Panel>

        {/* Audit Log Tab */}
        <Tabs.Panel value="audit" pt="md">
          <RBACAuditLogPanel
            tenantId={tenantId}
            autoRefresh={autoRefresh}
            pollingInterval={pollingInterval}
            isLoading={rbacLoading}
          />
        </Tabs.Panel>
      </Tabs>

      {/* Architecture Compliance Notice */}
      <Box mt="xl" p="md" style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
        <Text size="xs" c="dimmed">
          <strong>FERP Architecture Compliance:</strong> Repository pattern • Backend Snowflake ID
          generation • Data-driven RBAC • Life-critical safety • Immutable audit trails
        </Text>
        <Text size="xs" c="dimmed" mt="xs">
          <strong>Data Source:</strong> All role data from RoleRepository via GraphQL • Role
          inheritance from backend resolution • Real-time updates via polling (
          {pollingInterval / 1000}s interval)
        </Text>
        <Text size="xs" c="dimmed" mt="xs">
          <strong>Life-Critical Validation:</strong> Role change audit trails • Multi-tenant
          isolation • Permission inheritance validation • Admin operation logging
        </Text>
      </Box>

      {/* Week 2 MVP Notice */}
      <Box
        mt="md"
        p="md"
        style={{ border: '1px solid #339af0', borderRadius: 4, backgroundColor: '#e7f5ff' }}
      >
        <Text size="xs" fw={500} c="blue">
          <strong>Week 2 RBAC Implementation:</strong> Role Management Dashboard
        </Text>
        <Text size="xs" c="dimmed" mt="xs">
          This dashboard provides comprehensive RBAC management for tenant-wide roles and
          permissions. Backend GraphQL API (Week 1) is production-ready with role inheritance,
          immutable audit trails, and DynamoDB persistence.
        </Text>
        <Text size="xs" c="dimmed" mt="xs">
          <strong>Business Value:</strong> Enterprise-grade access control • Compliance audit
          trails • Role-based security • Multi-tenant isolation
        </Text>
      </Box>
    </Container>
  );
}
